import type { MapHitEvent, MapInteractionsConfig, MapProvider } from '@gpx-spa/geo-domain';
import type { LatLng } from '@gpx-spa/geo-domain';
import {
  isMapInteractionsDebug,
  logMapInteractions,
  summarizeInteractionsConfig,
} from '@gpx-spa/geo-domain';
import {
  computed,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRef,
  type Ref,
} from 'vue';

function screenPointFromNativeEvent(
  nativeEvent: unknown,
): { x: number; y: number } | null {
  if (!nativeEvent || typeof nativeEvent !== 'object') return null;
  const e = nativeEvent as { containerPoint?: { x: number; y: number } };
  if (e.containerPoint && typeof e.containerPoint.x === 'number') {
    return { x: e.containerPoint.x, y: e.containerPoint.y };
  }
  return null;
}

function screenPointFromLatLng(
  map: MapProvider | undefined,
  latlng: LatLng,
): { x: number; y: number } | null {
  if (!map) return null;
  const p = map.latLngToContainerPoint(latlng);
  return p ?? null;
}

function resolveScreenPoint(
  event: MapHitEvent,
  map: MapProvider | undefined,
): { x: number; y: number } | null {
  const fromLatLng = screenPointFromLatLng(map, event.latlng);
  if (fromLatLng) return fromLatLng;
  return screenPointFromNativeEvent(event.nativeEvent);
}

export type UseMapInteractionsOptions = {
  /** When false, does not register on the map (config still tracked). Default true. */
  enabled?: MaybeRef<boolean>;
};

export function useMapInteractions(
  mapProvider: MaybeRef<MapProvider | undefined>,
  config: MaybeRef<MapInteractionsConfig | undefined>,
  options: UseMapInteractionsOptions = {},
): {
  lastHit: Ref<MapHitEvent | null>;
  /** Latest hover hit; cleared on debounced pointer leave ({@link MapInteractionsConfig.onHoverEnd}). */
  lastHoverHit: Ref<MapHitEvent | null>;
  screenPoint: Ref<{ x: number; y: number } | null>;
  isRegistered: ComputedRef<boolean>;
  /** Re-register wrapped config (call after map layers are painted). */
  reapply: () => void;
} {
  const lastHit = ref<MapHitEvent | null>(null);
  const lastHoverHit = ref<MapHitEvent | null>(null);
  const screenPoint = ref<{ x: number; y: number } | null>(null);
  const isRegistered = computed(() => toValue(options.enabled) !== false);

  let moveEndUnsub: (() => void) | null = null;

  function refreshScreenPointFromActiveHit(): void {
    const hit = lastHoverHit.value ?? lastHit.value;
    const map = toValue(mapProvider);
    if (!hit || !map) return;
    const point = resolveScreenPoint(hit, map);
    if (point) {
      screenPoint.value = point;
    }
  }

  function buildConfig(): MapInteractionsConfig | null {
    const base = toValue(config);
    if (!base || !isRegistered.value) return null
    const userOnHit = base.onHit
    const userOnHoverEnd = base.onHoverEnd
    return {
      ...base,
      onHit(event) {
        lastHit.value = event
        if (event.trigger === 'hover') {
          lastHoverHit.value = event
        }
        const point = resolveScreenPoint(event, toValue(mapProvider))
        if (point) {
          screenPoint.value = point
        }
        return userOnHit(event)
      },
      onHoverEnd() {
        lastHoverHit.value = null
        const keepPointForClickOverlay =
          lastHit.value?.trigger === 'click' &&
          lastHit.value.target.type === 'marker' &&
          lastHit.value.target.marker.id.includes('hover-card')
        if (!keepPointForClickOverlay) {
          screenPoint.value = null
        }
        userOnHoverEnd?.()
      },
    }
  }

  function apply() {
    moveEndUnsub?.()
    moveEndUnsub = null

    const map = toValue(mapProvider)
    if (!map) return
    const built = buildConfig()
    if (isMapInteractionsDebug()) {
      logMapInteractions('useMapInteractions', 'apply → setInteractions', {
        ...summarizeInteractionsConfig(built),
        enabled: isRegistered.value,
      })
    }
    map.setInteractions(built)
    moveEndUnsub = map.onMoveEnd(() => {
      refreshScreenPointFromActiveHit()
    })
  }

  watch([() => toValue(mapProvider), () => toValue(config), () => toValue(options.enabled)], apply, {
    deep: true,
    immediate: true,
  })

  onBeforeUnmount(() => {
    moveEndUnsub?.()
    moveEndUnsub = null
    const map = toValue(mapProvider)
    map?.setInteractions(null)
    lastHit.value = null
    lastHoverHit.value = null
    screenPoint.value = null
  })

  return { lastHit, lastHoverHit, screenPoint, isRegistered, reapply: apply }
}
